import { HttpClient, HttpHeaders, HttpResponse } from '@angular/common/http';
import { inject, Inject, Injectable, SecurityContext } from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import { gql } from 'apollo-angular';
import { filter, lastValueFrom, map, Subject } from 'rxjs';
import { AbstractDocumentsService, QueryManagerService } from 'finzzle-angular-utils';
import JSZip from 'jszip';
import { FILE_TAGS } from '../../constants';
import {
  DeleteDocumentGQL,
  DocumentFilterConfigurationInput,
  SignatureDocumentFilterValues,
  DocumentsByFilterGQL,
  DocumentsByFilterPaginatedGQL,
  DocumentsGetDocumentMedatadasByIdGQL,
  DocumentsGetDocumentUploadUriByIdGQL,
  DocumentsSignatureStateByFilterGQL,
  FileMetadata,
  FileMetadataFilterInput,
  GedCategory,
  GedCreateEmptyGedFileGQL,
  GedDocumentType,
  InvestisseurDocumentsGQL,
  PaginatedResultOfFileMetadata,
  SignatureDocumentsFilterValuesGQL,
  SignatureState,
  UpdateDocumentMetadatasCommandInput,
  UpdateFileMetadataGQL,
} from '../models/generated/graphql';
import { ReferenceDataService } from './reference-data.service';
export interface ZipBlobItem {
  name: string;
  lastModified: Date;
  input: Blob;
}
const SIZE_LIMIT = 19 * 1024 * 1024; // 20MB
const UPLOAD_TEMPLATE_FILE = gql`
  mutation uploadTemplateFile($templateUrl: String!, $templateName: String!) {
    uploadTemplateFile(templateUrl: $templateUrl, templateName: $templateName)
  }
`;
const CREATE_EMPTY_GED_FILE_MUTATION = gql`
  mutation createEmptyGedFile(
    $fileName: String!
    $typeCode: String!
    $denomination: String!
    $investisseurId: Long!
    $dateFinValidite: DateTime
  ) {
    createEmptyGedFile(
      denomination: $denomination
      nomDeFichier: $fileName
      investisseurId: $investisseurId
      typeCode: $typeCode
      dateFinValidite: $dateFinValidite
    ) {
      fileId
      versionNumber
      url
      investisseurId
      extension
      denomination
      typeDocumentCode
      dateDeCreation
      creePar
      dateDeDerniereModification
      derniereModificationPar
      fileName
      dateSignature
      urlExpireDate
      permission
      typeLibelle
      dateFinValidite
    }
  }
`;
@Injectable({
  providedIn: 'root',
})
export class DocumentsService extends AbstractDocumentsService {
  private referenceDataService = inject(ReferenceDataService);
  private createEmptyFileGQL = inject(GedCreateEmptyGedFileGQL);
  private getFileMetadatasByIdGQL = inject(DocumentsGetDocumentMedatadasByIdGQL);
  private getDocumentsGetDocumentUploadUriByIdGQL = inject(DocumentsGetDocumentUploadUriByIdGQL);
  private getDocumentsByFilterPaginatedGQL = inject(DocumentsByFilterPaginatedGQL);
  private getDocumentsByFilterGQL = inject(DocumentsByFilterGQL);
  private getDocumentsSignatureStateByFilterGQL = inject(DocumentsSignatureStateByFilterGQL);
  private updateFileMatadataGQL = inject(UpdateFileMetadataGQL);
  private deleteDocumentGQL = inject(DeleteDocumentGQL);
  private investisseurDocumentsGQL = inject(InvestisseurDocumentsGQL);
  private signatureDocumentsFilterValuesGQL = inject(SignatureDocumentsFilterValuesGQL);
  private refreshDataSubject = new Subject<void>();
  public refreshData$ = this.refreshDataSubject.asObservable();
  constructor(
    http: HttpClient,
    domSanitizer: DomSanitizer,
    private queryManagerService: QueryManagerService,
    @Inject('environment')
    environment: any
  ) {
    super(http, domSanitizer, environment);
  }
  //#region Graphql requests
  override async createEmptyFile(
    fileName: string,
    ownerId: number,
    denomination: string | undefined
  ): Promise<
    | {
        url: string;
        fileId: string;
      }
    | null
    | undefined
  > {
    const result = await lastValueFrom(
      this.queryManagerService.mutate<{
        createEmptyGedFile: FileMetadata;
      }>({
        mutation: CREATE_EMPTY_GED_FILE_MUTATION,
        variables: {
          fileName: fileName,
          denomination: denomination,
          investisseurId: ownerId,
        },
      })
    );
    if (result.data!.createEmptyGedFile?.fileId) {
      return { fileId: result.data!.createEmptyGedFile?.fileId ?? '', url: result.data!.createEmptyGedFile?.url ?? '' };
    }
    return null;
  }
  async createEmptyFileWithType(
    fileName: string,
    investisseurIds: number[],
    denomination: string,
    documentType: GedDocumentType,
    dateFinValidite?: Date,
    dateSignature?: Date,
    isHandwrittenSignature?: boolean,
    tags?: string[],
    categorieCode?: GedCategory,
    dateDeReference?: Date,
    withDefaultTag = true
  ): Promise<
    | {
        url: string;
        fileId: string;
      }
    | null
    | undefined
  > {
    // Sans le tag par défaut, le document ne remonte pas dans l'onglet Documents de l'investisseur (filtré sur ce tag).
    const defaultTags = withDefaultTag ? [FILE_TAGS.DocumentMinotaure] : [];
    const tagsWithDefault = Array.from(new Set([...(tags ?? []), ...defaultTags]));
    categorieCode = categorieCode ?? (await this.getCategorieCode(documentType));
    const variables = {
      fileName: fileName,
      denomination: denomination,
      investisseurId: investisseurIds[0],
      documentType: this.normalizeTypeDocument(documentType),
      dateFinValidite: dateFinValidite,
      tags: tagsWithDefault,
      dateSignature: dateSignature,
      isHandwrittenSignature: isHandwrittenSignature,
      investisseurIds: investisseurIds,
      categorieCode: categorieCode,
      dateDeReference: dateDeReference ? this.toLocalDate(dateDeReference) : undefined,
    };
    const result = await lastValueFrom(
      this.createEmptyFileGQL.mutate({
        variables: variables,
      })
    );
    if (result.error) throw result.error;
    if (result.data!.createFileMetadata?.fileId) {
      return { fileId: result.data!.createFileMetadata?.fileId ?? '', url: result.data!.createFileMetadata?.url ?? '' };
    }
    return null;
  }
  async getFileMetadatasById(fileId: string): Promise<FileMetadata | null> {
    const result = await this.queryManagerService.handleQueryErrorsAsync(
      this.getFileMetadatasByIdGQL.fetch({
        fetchPolicy: 'network-only',
        variables: { fileId: fileId },
      })
    );
    if (result.data!.fileMetadataById) return result.data!.fileMetadataById as FileMetadata;
    return null;
  }
  async getUploadUrlLink(fileId: string): Promise<string | null> {
    const result = await this.queryManagerService.handleQueryErrorsAsync(
      this.getDocumentsGetDocumentUploadUriByIdGQL.fetch({
        fetchPolicy: 'network-only',
        variables: { fileId: fileId },
      })
    );
    if (result.data!.fileUploadUriById) return result.data!.fileUploadUriById;
    return null;
  }
  async uploadTemplateFile(templateUrl: string, templateName: string): Promise<string | null> {
    const result = await lastValueFrom(
      this.queryManagerService.mutate<{
        uploadTemplateFile: string;
      }>({
        mutation: UPLOAD_TEMPLATE_FILE,
        variables: { templateUrl: templateUrl, templateName: templateName },
      })
    );
    return result.data!.uploadTemplateFile ?? null;
  }
  async getDocumentsByFilterPaginated(
    documentFilterConfiguration: DocumentFilterConfigurationInput
  ): Promise<PaginatedResultOfFileMetadata | null> {
    const result = await this.queryManagerService.handleQueryErrorsAsync(
      this.getDocumentsByFilterPaginatedGQL.fetch({
        fetchPolicy: 'network-only',
        variables: { documentFilterConfiguration },
      })
    );
    return result.data!.fileMetadataByFiltersPaginated as PaginatedResultOfFileMetadata;
  }
  async getDocumentsByFilter(documentFilterConfiguration: DocumentFilterConfigurationInput): Promise<FileMetadata[] | null> {
    const result = await this.queryManagerService.handleQueryErrorsAsync(
      this.getDocumentsByFilterGQL.fetch({
        fetchPolicy: 'network-only',
        variables: { documentFilterConfiguration },
      })
    );
    return result.data!.fileMetadataByFilters as FileMetadata[];
  }
  async getDocumentsSignatureStateByFilter(documentFilterConfiguration: DocumentFilterConfigurationInput): Promise<FileMetadata[] | null> {
    const result = await lastValueFrom(
      this.getDocumentsSignatureStateByFilterGQL.fetch({
        fetchPolicy: 'network-only',
        variables: { documentFilterConfiguration },
      })
    );
    return result.data!.fileMetadataByFilters as FileMetadata[];
  }
  async updateFileMetadata(fileId: string, newMetadata: UpdateDocumentMetadatasCommandInput): Promise<FileMetadata | null> {
    newMetadata.derniereModificationPar = 'Minotaure';
    newMetadata.documentId = fileId;
    const result = await this.queryManagerService.handleMutationErrorsAsync(
      this.updateFileMatadataGQL.mutate({
        variables: { fileId, newMetadata },
      })
    );
    return result.data!.updateFileMetadata as FileMetadata;
  }
  async deleteDocument(documentId: string): Promise<boolean | undefined> {
    const result = await this.queryManagerService.handleMutationErrorsAsync(
      this.deleteDocumentGQL.mutate({
        variables: { documentId },
      })
    );
    return result.data!.deleteDocument;
  }
  public async getInvestisseurDocumentsByInvestisseurId(
    investisseurId: number,
    includeCancelled = false
  ): Promise<FileMetadata[] | undefined> {
    const where: FileMetadataFilterInput = {
      actif: { eq: true },
      tags: { some: { in: [FILE_TAGS.DocumentMinotaure, FILE_TAGS.DocumentAriane] } },
    };
    if (!includeCancelled) {
      where.or = [{ etatSignature: { neq: SignatureState.Cancelled } }, { etatSignature: { eq: null } }];
    }
    const result = await lastValueFrom(
      this.investisseurDocumentsGQL.fetch({
        fetchPolicy: 'network-only',
        variables: { investisseurId, where },
      })
    );
    if (result.error) throw result.error;
    return result?.data?.investisseurFiles as FileMetadata[];
  }
  public async getSignatureDocumentFilterValues(
    documentFilterConfiguration: DocumentFilterConfigurationInput
  ): Promise<SignatureDocumentFilterValues> {
    const result = await this.queryManagerService.handleQueryErrorsAsync(
      this.signatureDocumentsFilterValuesGQL.fetch({
        fetchPolicy: 'network-only',
        variables: { documentFilterConfiguration },
      })
    );
    return result.data!.signatureDocumentFilterValues as SignatureDocumentFilterValues;
  }
  //#endregion
  //#region Utils
  getMimeType(fileExtension: string): string | undefined {
    const mimeTypes: Record<string, string> = {
      pdf: 'application/pdf',
      png: 'image/png',
      jpg: 'image/jpeg',
      jpeg: 'image/jpeg',
      gif: 'image/gif',
      webp: 'image/webp',
    };
    return mimeTypes[fileExtension.replace(/^\./, '').toLowerCase()];
  }
  isAuthorizedFileSize(file: File): boolean {
    return file.size < SIZE_LIMIT;
  }
  isAuthorizedFileType(file: File): boolean {
    //TODO
    console.warn('implements', file.name);
    return true; //file.type ===  PDF_MIME_TYPE || file.type === JPEG_MIME_TYPE || file.type === PNG_MIME_TYPE;
  }
  isAuthorizedConsultantFile(file: File): boolean {
    return this.isAuthorizedFileSize(file) && this.isAuthorizedFileType(file);
  }
  isViewerCompatible(extension: string): boolean {
    //TODO
    console.warn('TODO implements ?', extension);
    return true;
    // const READABLE = [...JPEG_EXTENSIONS, ...PDF_EXTENSIONS, ...PNG_EXTENSIONS];
    // return READABLE.includes(extension.toLowerCase());
  }
  public async createFileAndUpload(
    file: File,
    fileName: string,
    investisseurIds: number[],
    denomination: string,
    typeCode: GedDocumentType,
    dateFinValidite?: Date,
    dateSignature?: Date,
    isHandwrittenSignature?: boolean,
    dateDeReference?: Date,
    tags?: string[],
    categorieCode?: GedCategory
  ): Promise<string | undefined> {
    const document = await this.createEmptyFileWithType(
      fileName,
      investisseurIds,
      denomination,
      typeCode,
      dateFinValidite,
      dateSignature,
      isHandwrittenSignature,
      tags,
      categorieCode,
      dateDeReference
    );
    if (document?.url) {
      await this.uploadBlobFileContent(file, document?.url);
    }
    return document?.fileId;
  }
  public async downloadDocumentAsFile(url: string): Promise<File> {
    return await lastValueFrom(
      this.http
        .get(url, {
          observe: 'response',
          responseType: 'blob',
        })
        .pipe(
          map((response: HttpResponse<Blob>) => {
            const blob = response.body!;
            const filename = this.getFileNameFromHeaders(response.headers) ?? '';
            return new File([blob], filename, { type: blob.type });
          })
        )
    );
  }
  public async downloadDocumentsAsZip(
    files: {
      url: string;
      name: string;
    }[],
    fileName: string
  ) {
    const zip = new JSZip();
    for (const file of files) {
      const response = await fetch(file.url);
      if (!response.ok) throw "Erreur lors du téléchargement d'un ou plusieurs documents";
      const blob = await response.blob();
      const filename = file.name || 'file';
      zip.file(filename, blob);
    }
    const content = await zip.generateAsync({ type: 'blob' });
    const blobUrl = URL.createObjectURL(content);
    const anchor = document.createElement('a');
    anchor.href = blobUrl;
    anchor.download = fileName + '.zip';
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
    URL.revokeObjectURL(blobUrl);
  }
  public async downloadDocument(file: { url: string; name: string }) {
    const response = await fetch(file.url);
    const blob = await response.blob();
    const blobUrl = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = blobUrl;
    anchor.download = file.name;
    anchor.click();
    URL.revokeObjectURL(blobUrl);
  }
  public async openDocumentInNewTab(file: { url: string; name: string; mimeType: string }) {
    const newTab = window.open('', '_blank');
    if (!newTab) return;

    try {
      const response = await fetch(file.url);
      if (!response.ok) throw new Error('Erreur lors du téléchargement du document');

      const blob = await response.blob();
      const documentBlob = file.mimeType ? new Blob([blob], { type: file.mimeType }) : blob;
      const blobUrl = URL.createObjectURL(documentBlob);
      newTab.addEventListener('load', () => URL.revokeObjectURL(blobUrl), { once: true });
      newTab.location.href = blobUrl;
    } catch (error) {
      newTab.close();
      throw error;
    }
  }
  public triggerRefreshData(): void {
    this.refreshDataSubject.next();
  }
  // FIXME : Temporary trick to avoid error with different GedDocumentType enum generated.
  // Error occured with type : IMPOT_2031, IMPOT_2035, IMPOT_2042, IMPOT_2044, IMPOT_2139, ATT_85A.
  public normalizeTypeDocument(typeCode: string | undefined): GedDocumentType {
    if (Object.values(GedDocumentType).includes(typeCode as GedDocumentType)) {
      return typeCode as GedDocumentType;
    } else if (typeCode && ['IMPOT_2031', 'IMPOT_2035', 'IMPOT_2042', 'IMPOT_2044', 'IMPOT_2139', 'ATT_85A'].includes(typeCode)) {
      return typeCode.replace('_', '') as GedDocumentType;
    }
    return GedDocumentType.ValeurNonSupportee;
  }
  public async getCategorieCode(documentType: GedDocumentType): Promise<GedCategory> {
    const record = await lastValueFrom(this.referenceDataService.getDocumentTypeCategoryRecords().pipe(filter(v => v !== null)));
    return (record?.[documentType] ?? 'AUTRE') as GedCategory;
  }
  private getFileNameFromHeaders(headers: HttpHeaders) {
    const contentDisposition = headers?.get('Content-Disposition');
    if (contentDisposition) {
      const match = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
      if (match && match[0]) {
        return match[0].replace('filename=', '') ?? '';
      }
    }
    return null;
  }

  private toLocalDate(date: Date): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');

    return `${year}-${month}-${day}`;
  }

  public sanitizeUrl(url: string | null): string | null {
    return this.domSanitizer.sanitize(SecurityContext.URL, url);
  }
}
